import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiCheck, FiSearch, FiDownload, FiPrinter, FiSettings } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import {
  getAddressBook, getPendingAddresses,
  createAddressBookEntry, updateAddressBookEntry,
  deleteAddressBookEntry, approveAddressBookEntry,
  exportReportTable, getDerasarMembers,
} from '../../services/api';
import { usePdfDownload } from '../../pdf/usePdfDownload';
import PdfOptionsModal from '../../pdf/PdfOptionsModal';
import { ExcelExportModal, StateCitySelect } from '../../components/common';
import styles from './AddressBookPage.module.css';

const ADDRESS_COLUMNS = [
  { key: 'flatNo', label: 'Flat No / Bunglow No' },
  { key: 'buildingName', label: 'Building / Bunglow Name' },
  { key: 'addressLine1', label: 'Address Line 1' },
  { key: 'addressLine2', label: 'Address Line 2' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'pincode', label: 'Pincode' },
];

const ADDR_FIELDS_BEFORE_LOCATION = [
  { key: 'flatNo' },
  { key: 'buildingName' },
  { key: 'addressLine1', fullRow: true },
  { key: 'addressLine2', fullRow: true },
];
const ADDR_FIELDS_AFTER_LOCATION = [{ key: 'pincode' }, { key: 'country' }];

// Mirrors the "Belongs To Member" search used in the admin Add Person popup
// (UsersPage.js) — shows each member's name with email/phone for disambiguation.
const MemberSearchSelect = ({ members, value, onChange, placeholder = 'Search member by name, email or phone…' }) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const selected = members.find((m) => String(m.id) === String(value));
  const displayVal = isOpen ? search : (selected ? selected.name : '');
  const filtered = members.filter((m) => {
    const q = search.toLowerCase();
    return (m.name || '').toLowerCase().includes(q)
      || (m.email || '').toLowerCase().includes(q)
      || (m.phone || '').toLowerCase().includes(q);
  });
  return (
    <div style={{ position: 'relative' }} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setIsOpen(false); }}>
      <input
        className={styles.input}
        placeholder={placeholder}
        value={displayVal}
        onChange={(e) => { setSearch(e.target.value); setIsOpen(true); onChange(''); }}
        onFocus={() => setIsOpen(true)}
      />
      {isOpen && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, maxHeight: 220, overflowY: 'auto', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
          {filtered.length === 0
            ? <div style={{ padding: '8px 12px', color: 'var(--color-text-muted)' }}>No members found</div>
            : filtered.map((m) => (
              <div key={m.id} tabIndex={0}
                style={{ padding: '8px 12px', borderBottom: '1px solid var(--color-border)', cursor: 'pointer' }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onChange(String(m.id)); setIsOpen(false); setSearch(''); }}
              >
                <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{m.name}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>{m.email}{m.phone && ` · ${m.phone}`}</div>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
};

const emptyForm = {
  label: '',
  memberId: '',
  flatNo: '',
  buildingName: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  pincode: '',
  country: 'India',
  isDefault: false,
};

const formatFieldLabel = (field) => field.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());

export default function AddressBookPage() {
  const { activeDerasarId, activeDerasar, user, isDerasarAdmin } = useAuth();
  const isReadOnlyAdmin = !!isDerasarAdmin || user?.role === 'super_admin' || user?.role === 'platform_admin';

  const [entries, setEntries] = useState([]);
  const [pending, setPending] = useState([]);
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [approving, setApproving] = useState(null);
  const [approveForm, setApproveForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showPdfModal,   setShowPdfModal]   = useState(false);
  const [showExcelModal, setShowExcelModal] = useState(false);

  const { download: downloadPdf, downloading: pdfDownloading, hasSaved: hasSavedPdf } = usePdfDownload('addressBook');

  const load = useCallback(async () => {
    if (!activeDerasarId) return;
    try {
      const requests = [getAddressBook(activeDerasarId)];
      if (isReadOnlyAdmin) requests.push(getPendingAddresses(activeDerasarId));
      const [entriesRes, pendingRes] = await Promise.all(requests);
      setEntries(Array.isArray(entriesRes.data) ? entriesRes.data : []);
      setPending(Array.isArray(pendingRes?.data) ? pendingRes.data : []);
    } catch {
      toast.error('Failed to load address book');
    }

    if (isReadOnlyAdmin) {
      try {
        const membersRes = await getDerasarMembers(activeDerasarId);
        setMembers(Array.isArray(membersRes?.data) ? membersRes.data : []);
      } catch {
        setMembers([]);
      }
    }
  }, [activeDerasarId, isReadOnlyAdmin]);

  useEffect(() => { load(); }, [load]);

  const filteredEntries = useMemo(() => {
    const query = search.toLowerCase().trim();
    if (!query) return entries;
    return entries.filter((entry) => [
      entry.label,
      entry.memberOwner?.name,
      entry.flatNo,
      entry.buildingName,
      entry.addressLine1,
      entry.addressLine2,
      entry.city,
      entry.state,
      entry.pincode,
    ].some((value) => (value || '').toLowerCase().includes(query)));
  }, [entries, search]);

  const toExportRow = (e) => ({
    label:        e.label        || '',
    memberName:   e.memberOwner?.name || '',
    flatNo:       e.flatNo       || '',
    buildingName: e.buildingName || '',
    addressLine1: e.addressLine1 || '',
    addressLine2: e.addressLine2 || '',
    city:         e.city         || '',
    state:        e.state        || '',
    pincode:      e.pincode      || '',
  });

  const addressBookPdfProps = useMemo(() => ({
    derasar:  activeDerasar?.derasarId,
    filters:  search.trim() ? `Search: "${search.trim()}"` : '',
    rows:     filteredEntries.map(toExportRow),
    isAdmin:  isReadOnlyAdmin,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [filteredEntries, search, isReadOnlyAdmin]);

  const buildExcelPayload = () => {
    const cols = [
      { key: 'label',        label: 'Label',              weight: 1.4 },
      ...(isReadOnlyAdmin ? [{ key: 'memberName', label: 'Member', weight: 1.4 }] : []),
      { key: 'flatNo',       label: 'Flat No',            weight: 0.9 },
      { key: 'buildingName', label: 'Building Name',      weight: 1.2 },
      { key: 'addressLine1', label: 'Address Line 1',     weight: 1.8 },
      { key: 'addressLine2', label: 'Address Line 2',     weight: 1.8 },
      { key: 'city',         label: 'City',               weight: 0.9 },
      { key: 'state',        label: 'State',              weight: 0.9 },
      { key: 'pincode',      label: 'Pincode',            weight: 0.8 },
    ];
    return {
      title:     'Address Book',
      derasarId: activeDerasarId,
      filters:   search.trim() ? `Search: "${search.trim()}"` : '',
      columns:   cols,
      rows:      filteredEntries.map(toExportRow),
      totals:    { label: `Total: ${filteredEntries.length} address${filteredEntries.length !== 1 ? 'es' : ''}` },
    };
  };

  const handleDownloadExcel = async () => {
    try {
      const res = await exportReportTable({ ...buildExcelPayload(), format: 'excel' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `address-book-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error('Failed to export Excel');
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (entry) => {
    setEditing(entry);
    setForm({
      label: entry.label || '',
      memberId: entry.memberId || '',
      flatNo: entry.flatNo || '',
      buildingName: entry.buildingName || '',
      addressLine1: entry.addressLine1 || '',
      addressLine2: entry.addressLine2 || '',
      city: entry.city || '',
      state: entry.state || '',
      pincode: entry.pincode || '',
      country: entry.country || 'India',
      isDefault: !!entry.isDefault,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.label.trim()) return toast.error('Label is required');
    if (isReadOnlyAdmin && !editing && !form.memberId) return toast.error('Please select a member');
    setSaving(true);
    try {
      const payload = { ...form, derasarId: activeDerasarId };
      if (editing) {
        await updateAddressBookEntry(editing.id, payload);
        toast.success('Address updated');
      } else {
        await createAddressBookEntry(payload);
        toast.success('Address added');
      }
      setShowModal(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (entry) => {
    if (!window.confirm(`Deactivate "${entry.label}"?`)) return;
    try {
      await deleteAddressBookEntry(entry.id);
      toast.success('Address deactivated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    }
  };

  const openApprove = (person) => {
    setApproving(person);
    setApproveForm({
      label: `${person.name}'s Address`,
      flatNo: person.flatNo || '',
      buildingName: person.buildingName || '',
      addressLine1: person.addressLine1 || '',
      addressLine2: person.addressLine2 || '',
      city: person.city || '',
      state: person.state || '',
      pincode: person.pincode || '',
      country: person.country || 'India',
      isDefault: false,
    });
  };

  const handleApprove = async () => {
    if (!approving) return;
    try {
      await approveAddressBookEntry(approving.id, approveForm);
      toast.success('Address approved and added to member address book');
      setApproving(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Approve failed');
    }
  };

  const set = (field) => (e) =>
    setForm((current) => ({ ...current, [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const setA = (field) => (e) =>
    setApproveForm((current) => ({ ...current, [field]: e.target.value }));

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Address Book</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => hasSavedPdf ? downloadPdf(addressBookPdfProps) : setShowPdfModal(true)}
            disabled={pdfDownloading || filteredEntries.length === 0}
          >
            {hasSavedPdf ? <FiPrinter size={14} /> : <FiSettings size={14} />} PDF
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setShowExcelModal(true)}
            disabled={filteredEntries.length === 0}
          >
            <FiDownload size={14} /> Excel
          </button>
          <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={openAdd}>
            <FiPlus size={14} /> Add Address
          </button>
        </div>
      </div>

      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}>
          Address Book
          <span className={styles.countBadge}>{filteredEntries.length}</span>
        </div>

        <div className={styles.searchBar}>
          <FiSearch className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            placeholder="Search by label, member, flat no, building, address, city, state, pincode..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className={styles.memberTableContainer}>
          <table className={styles.memberTable}>
            <thead>
              <tr>
                <th>Label</th>
                {isReadOnlyAdmin && <th>Member</th>}
                {ADDRESS_COLUMNS.map((column) => <th key={column.key}>{column.label}</th>)}
                <th>Status</th>
                <th className={styles.actionsCell}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.length === 0 && (
                <tr>
                  <td colSpan={11} className={styles.emptyState}>
                    {search ? 'No addresses match your search' : 'No addresses yet'}
                  </td>
                </tr>
              )}
              {filteredEntries.map((entry) => (
                <tr key={entry.id}>
                  <td>
                    <div className={styles.labelCell}>
                      <span>{entry.label}</span>
                      {!isReadOnlyAdmin && entry.isDefault && (
                        <span className={`${styles.badge} ${styles.badgeDefault}`}>Default for member</span>
                      )}
                    </div>
                  </td>
                  {isReadOnlyAdmin && <td>{entry.memberOwner?.name || '-'}</td>}
                  {ADDRESS_COLUMNS.map((column) => (
                    <td key={column.key}>{entry[column.key] || '-'}</td>
                  ))}
                  <td>
                    <span className={entry.isActive ? styles.badge : styles.badgeInactive}>
                      {entry.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className={styles.actionsCell}>
                    <div className={styles.actions}>
                      <button className={`${styles.btn} ${styles.btnEdit}`} onClick={() => openEdit(entry)}>
                        <FiEdit2 size={12} /> Edit
                      </button>
                      {entry.isActive && (
                        <button className={`${styles.btn} ${styles.btnDanger}`} onClick={() => handleDeactivate(entry)}>
                          <FiTrash2 size={12} /> Deactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {isReadOnlyAdmin && pending.length > 0 && (
        <div className={styles.sectionCard}>
          <div className={styles.sectionHeader}>
            Pending Approvals
            <span className={styles.countBadge}>{pending.length}</span>
          </div>
          <div className={styles.memberTableContainer}>
            <table className={styles.memberTable}>
              <thead>
                <tr>
                  <th>Person</th>
                  {ADDRESS_COLUMNS.map((column) => <th key={column.key}>{column.label}</th>)}
                  <th className={styles.actionsCell}>Action</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((person) => (
                  <tr key={person.id}>
                    <td>{person.name}</td>
                    {ADDRESS_COLUMNS.map((column) => (
                      <td key={column.key}>{person[column.key] || '-'}</td>
                    ))}
                    <td className={styles.actionsCell}>
                      <button className={`${styles.btn} ${styles.btnApprove}`} onClick={() => openApprove(person)}>
                        <FiCheck size={12} /> Review & Approve
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className={styles.modal} onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div className={styles.modalBox}>
            <div className={styles.modalTitle}>{editing ? 'Edit Address' : 'Add Address'}</div>
            {isReadOnlyAdmin && !editing && (
              <div className={styles.formGroup}>
                <label className={styles.label}>Member *</label>
                <MemberSearchSelect
                  members={members}
                  value={form.memberId}
                  onChange={(val) => setForm((f) => ({ ...f, memberId: val }))}
                />
              </div>
            )}
            {isReadOnlyAdmin && editing && (
              <div className={styles.formGroup}>
                <label className={styles.label}>Member</label>
                <input className={styles.input} value={editing.memberOwner?.name || ''} disabled />
              </div>
            )}
            <div className={styles.formGroup}>
              <label className={styles.label}>Label *</label>
              <input className={styles.input} value={form.label} onChange={set('label')} placeholder="e.g. Home" />
            </div>
            <div className={styles.formGrid}>
              {ADDR_FIELDS_BEFORE_LOCATION.map(({ key, fullRow }) => (
                <div key={key} className={styles.formGroup} style={fullRow ? { gridColumn: '1 / -1' } : undefined}>
                  <label className={styles.label}>{formatFieldLabel(key)}</label>
                  <input className={styles.input} value={form[key]} onChange={set(key)} />
                </div>
              ))}
              <StateCitySelect
                state={form.state}
                city={form.city}
                onStateChange={(val) => setForm((f) => ({ ...f, state: val }))}
                onCityChange={(val) => setForm((f) => ({ ...f, city: val }))}
                formGroupClassName={styles.formGroup}
                labelClassName={styles.label}
              />
              {ADDR_FIELDS_AFTER_LOCATION.map(({ key }) => (
                <div key={key} className={styles.formGroup}>
                  <label className={styles.label}>{formatFieldLabel(key)}</label>
                  <input className={styles.input} value={form[key]} onChange={set(key)} />
                </div>
              ))}
            </div>
            <label className={styles.checkLabel}>
              <input type="checkbox" checked={form.isDefault} onChange={set('isDefault')} />
              Set as default address for member
            </label>
            <div className={styles.modalActions}>
              <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {approving && (
        <div className={styles.modal}>
          <div className={styles.modalBox}>
            <div className={styles.modalTitle}>Approve Address - {approving.name}</div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Label *</label>
              <input className={styles.input} value={approveForm.label} onChange={setA('label')} />
            </div>
            <div className={styles.formGrid}>
              {ADDR_FIELDS_BEFORE_LOCATION.map(({ key, fullRow }) => (
                <div key={key} className={styles.formGroup} style={fullRow ? { gridColumn: '1 / -1' } : undefined}>
                  <label className={styles.label}>{formatFieldLabel(key)}</label>
                  <input className={styles.input} value={approveForm[key]} onChange={setA(key)} />
                </div>
              ))}
              <StateCitySelect
                state={approveForm.state}
                city={approveForm.city}
                onStateChange={(val) => setApproveForm((f) => ({ ...f, state: val }))}
                onCityChange={(val) => setApproveForm((f) => ({ ...f, city: val }))}
                formGroupClassName={styles.formGroup}
                labelClassName={styles.label}
              />
              {ADDR_FIELDS_AFTER_LOCATION.map(({ key }) => (
                <div key={key} className={styles.formGroup}>
                  <label className={styles.label}>{formatFieldLabel(key)}</label>
                  <input className={styles.input} value={approveForm[key]} onChange={setA(key)} />
                </div>
              ))}
            </div>
            <div className={styles.modalActions}>
              <button className={`${styles.btn} ${styles.btnEdit}`} onClick={() => setApproving(null)}>Cancel</button>
              <button className={`${styles.btn} ${styles.btnApprove}`} onClick={handleApprove}>
                <FiCheck size={12} /> Approve & Add to Book
              </button>
            </div>
          </div>
        </div>
      )}

      {showPdfModal && (
        <PdfOptionsModal
          type="addressBook"
          documentProps={addressBookPdfProps}
          onClose={() => setShowPdfModal(false)}
        />
      )}

      {showExcelModal && (
        <ExcelExportModal
          onClose={() => setShowExcelModal(false)}
          onDownload={handleDownloadExcel}
          exportPayload={buildExcelPayload()}
        />
      )}
    </div>
  );
}
